import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { formatTime12 } from "@/components/ui/time-picker";
import { CalendarDays, Clock, MapPin, Loader2, Church, FileText, ChevronLeft, ChevronRight } from "lucide-react";
import { format, parseISO } from "date-fns";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { useState, useEffect, useRef } from "react";

// ─── Bulletin Swiper ──────────────────────────────────────────────────────────
interface BulletinData {
  name: string;
  service_type: string;
  service_date: string | null;
  service_time: string | null;
  event_id: string | null;
  bulletin_items: Array<{ id: string; label: string; person: string; time: string; type: string }>;
  next_week_items: Array<{ id: string; label: string; person: string; time: string; type: string }>;
  announcements: Array<{ id: string; text: string }>;
  notes: string | null;
  offering_snapshot?: { offering_heading?: string | null } | null;
}

interface ChurchInfo {
  church_name: string;
  logo_url: string | null;
  bible_verse: string | null;
  bible_reference: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
}

interface OfferingData {
  entries: Array<{ category: string; amount: number }>;
  titheContributions: Array<{ name: string; amount: number }>;
  titheEntries: Array<{ category: string; amount: number }>;
  offeringEntries: Array<{ category: string; amount: number }>;
  tithesTotal: number;
  total: number;
  date: string;
}

const FS = 13; // base font size px — matches bulletin preview

function renderItems(items: BulletinData["bulletin_items"]) {
  return items.map((item) => {
    if (item.type === "divider") return (
      <div key={item.id} style={{ borderTop: "1px solid #e5e7eb", margin: "4px 0" }} />
    );
    if (item.type === "header") return (
      <p key={item.id} style={{ fontWeight: 700, fontSize: `${FS * 1.2}px`, color: "#1e3a8a", paddingTop: 6 }}>{item.label}</p>
    );
    return (
      <div key={item.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #e5e7eb", paddingTop: 5, paddingBottom: 5 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {item.time && <span style={{ fontFamily: "monospace", fontSize: `${FS}px`, color: "#1f2937", width: "3rem" }}>{item.time}</span>}
          <span style={{ fontSize: `${FS}px`, color: "#1f2937" }}>{item.label}</span>
        </div>
        {item.person && <span style={{ fontStyle: "italic", fontSize: `${FS}px`, color: "#6b7280" }}>{item.person}</span>}
      </div>
    );
  });
}

function BulletinPreviewPage({
  data, church, pageIndex, checkinUrl, offeringData,
}: {
  data: BulletinData;
  church: ChurchInfo | null;
  pageIndex: number;
  checkinUrl: string | null;
  offeringData: OfferingData | null;
}) {
  const items = data.bulletin_items || [];
  const rawNextItems = data.next_week_items || [];
  const nextWeekHeading = rawNextItems.find((i) => i.id === "__next_week_heading__")?.label?.trim() || "Next Week's Order of Service";
  const nextItems = rawNextItems.filter((i) => i.id !== "__next_week_heading__");
  const rawAnnouncements = data.announcements || [];
  const announcementsHeading = rawAnnouncements.find((a) => a.id === "__announcements_heading__")?.text?.trim() || "Announcements";
  const announcements = rawAnnouncements.filter((a) => a.id !== "__announcements_heading__" && a.text?.trim());

  if (pageIndex === 0) return (
    <div style={{ fontSize: `${FS}px`, padding: "4% 5%", minHeight: 600 }}>
      {/* Church Header */}
      <div style={{ textAlign: "center", marginBottom: 12 }}>
        {church?.logo_url && (
          <img src={church.logo_url} alt="Logo" style={{ width: 64, height: 64, objectFit: "contain", margin: "0 auto 12px" }} />
        )}
        <h2 style={{ fontWeight: 700, fontSize: `${FS * 1.5}px`, color: "#1e3a8a", margin: 0 }}>{church?.church_name}</h2>
        {(church?.address || church?.city) && (
          <p style={{ fontSize: `${FS}px`, color: "#6b7280", margin: "4px 0 0" }}>
            {[church?.address, church?.city, church?.state].filter(Boolean).join(", ")}
          </p>
        )}
      </div>
      {/* Service Info */}
      <div style={{ textAlign: "center", marginBottom: 8 }}>
        <h3 style={{ fontWeight: 700, fontSize: `${FS * 1.2}px`, color: "#1e3a8a", margin: 0 }}>{data.service_type}</h3>
        {(data.service_date || data.service_time) && (
          <p style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontSize: `${FS}px`, color: "#6b7280", margin: "4px 0 0" }}>
            {data.service_date && (
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <CalendarDays style={{ width: 12, height: 12 }} />
                {format(new Date(data.service_date + "T00:00"), "MMMM d, yyyy")}
              </span>
            )}
            {data.service_time && (
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <Clock style={{ width: 12, height: 12 }} />
                {data.service_time.split(" - ").map(t => formatTime12(t.trim())).join(" – ")}
              </span>
            )}
          </p>
        )}
      </div>
      <div style={{ borderTop: "1px solid #e5e7eb", margin: "8px 0 6px" }} />
      {/* Order of Service */}
      <div>
        <h4 style={{ fontWeight: 600, textTransform: "uppercase", textAlign: "center", fontSize: `${FS * 0.85}px`, color: "#6b7280", letterSpacing: "0.09em", marginBottom: 2 }}>
          Order of Service
        </h4>
        {renderItems(items)}
      </div>
      {/* Bible verse */}
      {church?.bible_verse && (
        <div style={{ textAlign: "center", borderTop: "1px solid #e5e7eb", marginTop: 16, paddingTop: 12 }}>
          <p style={{ fontStyle: "italic", fontSize: `${FS * 0.85}px`, color: "#6b7280" }}>&ldquo;{church.bible_verse}&rdquo;</p>
          {church.bible_reference && (
            <p style={{ fontWeight: 500, fontSize: `${FS * 0.85}px`, color: "#6b7280", marginTop: 4 }}>— {church.bible_reference}</p>
          )}
        </div>
      )}
    </div>
  );

  if (pageIndex === 1) return (
    <div style={{ fontSize: `${FS}px`, padding: "10% 5%", minHeight: 600 }}>
      <div>
        <h4 style={{ fontWeight: 600, textTransform: "uppercase", textAlign: "center", fontSize: `${FS * 0.85}px`, color: "#6b7280", letterSpacing: "0.09em", marginBottom: 2 }}>
          {nextWeekHeading}
        </h4>
        {renderItems(nextItems)}
      </div>
      {data.notes && (
        <>
          <div style={{ borderTop: "1px solid #e5e7eb", margin: "8px 0" }} />
          <p style={{ fontStyle: "italic", textAlign: "center", fontSize: `${FS * 0.85}px`, color: "#6b7280" }}>{data.notes}</p>
        </>
      )}
    </div>
  );

  if (pageIndex === 2) return (
    <div style={{ fontSize: `${FS}px`, padding: "10% 5%", minHeight: 600 }}>
      {announcements.length > 0 && (
        <div>
          <h4 style={{ fontWeight: 600, textTransform: "uppercase", textAlign: "center", fontSize: `${FS * 0.85}px`, color: "#6b7280", letterSpacing: "0.09em", marginBottom: 2 }}>
            {announcementsHeading}
          </h4>
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {announcements.map((a) => (
              <li key={a.id} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                <span style={{ color: "#1e3a8a", fontWeight: 700 }}>•</span>
                <span style={{ fontSize: `${FS}px`, color: "#1f2937" }}>{a.text}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {/* QR check-in */}
      {checkinUrl && (
        <div style={{ borderTop: "1px solid #e5e7eb", marginTop: 12, paddingTop: 12, textAlign: "center" }}>
          <p style={{ fontWeight: 600, textTransform: "uppercase", fontSize: `${FS * 0.78}px`, color: "#6b7280", letterSpacing: "0.08em" }}>Attendance Check-in</p>
          <div style={{ display: "flex", justifyContent: "center", marginTop: 8 }}>
            <QRCodeSVG value={checkinUrl} size={96} level="H" includeMargin />
          </div>
          <p style={{ fontSize: `${FS * 0.65}px`, color: "#6b7280", marginTop: 6, wordBreak: "break-all" }}>{checkinUrl}</p>
        </div>
      )}
    </div>
  );

  if (pageIndex === 3) return (
    <div style={{ fontSize: `${FS}px`, padding: "10% 5%", minHeight: 600 }}>
      {offeringData && offeringData.entries.length > 0 ? (
        <div>
          <h4 style={{ fontWeight: 600, textTransform: "uppercase", textAlign: "center", fontSize: `${FS * 0.85}px`, color: "#6b7280", letterSpacing: "0.09em", marginBottom: 2 }}>
            {data.offering_snapshot?.offering_heading?.trim() || "Tithes & Offering"}
          </h4>
          {/* Tithe contributors */}
          {offeringData.titheContributions.length > 0 && (
            <div style={{ marginTop: 8 }}>
              {offeringData.titheContributions.map((tc, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: `${FS}px`, paddingTop: 3, paddingBottom: 3 }}>
                  <span style={{ color: "#6b7280" }}>{tc.name}</span>
                  <span style={{ color: "#6b7280" }}>${tc.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, borderTop: "1px solid #e5e7eb", paddingTop: 4, marginTop: 6, fontSize: `${FS}px` }}>
                <span style={{ color: "#1f2937" }}>Tithes Total</span>
                <span style={{ color: "#1f2937" }}>${offeringData.tithesTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          )}
          {/* Offering entries */}
          {offeringData.offeringEntries.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <p style={{ fontWeight: 700, fontSize: `${FS * 0.85}px`, color: "#6b7280", marginBottom: 2 }}>Offerings</p>
              {offeringData.offeringEntries.map((e) => (
                <div key={e.category} style={{ display: "flex", justifyContent: "space-between", fontSize: `${FS}px`, paddingTop: 5, paddingBottom: 5 }}>
                  <span style={{ color: "#1f2937" }}>{e.category}</span>
                  <span style={{ fontWeight: 700, color: "#1f2937" }}>${e.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
              ))}
            </div>
          )}
          {/* Total */}
          <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, borderTop: "1px solid #e5e7eb", paddingTop: 4, marginTop: 8, fontSize: `${FS}px` }}>
            <span style={{ color: "#1e3a8a" }}>Total Contributions</span>
            <span style={{ color: "#1e3a8a" }}>${offeringData.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
          </div>
          {offeringData.date && (
            <p style={{ textAlign: "center", fontSize: `${FS * 0.85}px`, color: "#6b7280", marginTop: 8 }}>
              {format(new Date(offeringData.date + "T00:00"), "MMMM d, yyyy")}
            </p>
          )}
        </div>
      ) : (
        <p style={{ textAlign: "center", color: "#9ca3af", fontSize: `${FS}px`, marginTop: 40 }}>No offering data for this date.</p>
      )}
    </div>
  );

  return null;
}

function BulletinSwiper({ bulletin, church, offeringData }: { bulletin: BulletinData; church: ChurchInfo | null; offeringData: OfferingData | null }) {
  const [page, setPage] = useState(0);
  const startXRef = useRef<number | null>(null);
  const total = 4;
  const checkinUrl = bulletin.event_id ? `${window.location.origin}/checkin?event_id=${bulletin.event_id}` : null;

  const goTo = (i: number) => setPage(Math.max(0, Math.min(i, total - 1)));
  const onTouchStart = (e: React.TouchEvent) => { startXRef.current = e.touches[0].clientX; };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (startXRef.current === null) return;
    const dx = e.changedTouches[0].clientX - startXRef.current;
    if (dx < -40) goTo(page + 1);
    else if (dx > 40) goTo(page - 1);
    startXRef.current = null;
  };

  const labels = ["Order of Service", "Next Week", "Announcements", "Tithes & Offering"];

  return (
    <div className="mt-10">
      <div className="flex items-center gap-2 border-b border-gray-200 pb-3 mb-4">
        <FileText className="w-5 h-5" style={{ color: "#1e3a8a" }} />
        <h2 className="text-lg font-bold text-gray-900">Service Bulletin</h2>
      </div>

      {/* Page label tabs */}
      <div className="flex gap-1 mb-3">
        {labels.map((label, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            style={{
              flex: 1,
              padding: "4px 6px",
              fontSize: 11,
              fontWeight: i === page ? 700 : 400,
              borderRadius: 6,
              border: i === page ? "1px solid #1e3a8a" : "1px solid #e5e7eb",
              background: i === page ? "#1e3a8a" : "white",
              color: i === page ? "white" : "#6b7280",
              cursor: "pointer",
              transition: "all 0.15s",
            }}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm select-none">
        <div
          className="flex transition-transform duration-300 ease-in-out"
          style={{ transform: `translateX(-${page * 100}%)` }}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {Array.from({ length: total }, (_, i) => (
            <div key={i} className="w-full shrink-0">
              <BulletinPreviewPage data={bulletin} church={church} pageIndex={i} checkinUrl={checkinUrl} offeringData={offeringData} />
            </div>
          ))}
        </div>
        {page > 0 && (
          <button onClick={() => goTo(page - 1)} className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 border border-gray-200 shadow flex items-center justify-center hover:bg-gray-50 transition-colors">
            <ChevronLeft className="w-4 h-4 text-gray-600" />
          </button>
        )}
        {page < total - 1 && (
          <button onClick={() => goTo(page + 1)} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 border border-gray-200 shadow flex items-center justify-center hover:bg-gray-50 transition-colors">
            <ChevronRight className="w-4 h-4 text-gray-600" />
          </button>
        )}
      </div>

      {/* Dot indicators */}
      <div className="flex justify-center gap-2 mt-3">
        {Array.from({ length: total }, (_, i) => (
          <button key={i} onClick={() => goTo(i)} style={{ width: 8, height: 8, borderRadius: "50%", background: i === page ? "#1e3a8a" : "#d1d5db", border: "none", cursor: "pointer", padding: 0 }} />
        ))}
      </div>
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────

function useCountdown(eventDate: string, startTime: string | null) {
  const [timeLeft, setTimeLeft] = useState<{ days: number; hours: number; minutes: number; seconds: number } | null>(null);
  const [isPast, setIsPast] = useState(false);

  useEffect(() => {
    const target = new Date(`${eventDate}T${startTime || "00:00:00"}`);
    const update = () => {
      const now = new Date();
      const diff = target.getTime() - now.getTime();
      if (diff <= 0) {
        setIsPast(true);
        setTimeLeft(null);
        return;
      }
      setIsPast(false);
      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [eventDate, startTime]);

  return { timeLeft, isPast };
}

export default function EventViewPage() {
  const [params] = useSearchParams();
  const eventId = params.get("id");

  const { data: event, isLoading } = useQuery({
    queryKey: ["public_event", eventId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("events")
        .select("id, name, event_type, event_date, start_time, end_time, location, description, flyer_url, church_id, category, public_view_enabled")
        .eq("id", eventId!)
        .eq("is_active", true)
        .eq("public_view_enabled", true)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!eventId,
  });

  const { data: bulletin } = useQuery({
    queryKey: ["public_event_bulletin", eventId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bulletin_templates")
        .select("id, name, service_type, service_date, service_time, bulletin_items, next_week_items, announcements, notes, event_id, offering_date, offering_snapshot")
        .eq("event_id", eventId!)
        .eq("is_template", false)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!eventId,
  });

  const { data: church } = useQuery({
    queryKey: ["public_church", event?.church_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("church_profile")
        .select("church_name, logo_url, bible_verse, bible_reference, address, city, state")
        .eq("id", event!.church_id!)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!event?.church_id,
  });

  // Prefer the snapshot saved with the bulletin; fall back to querying
  // finance_records for authenticated users (anon users are blocked by RLS).
  const offeringDate = (bulletin as any)?.offering_date || (bulletin as any)?.service_date || null;

  const snapshotData: OfferingData | null = (() => {
    const snapshot = (bulletin as any)?.offering_snapshot;
    if (!snapshot || !snapshot.entries || snapshot.entries.length === 0) return null;
    return { ...snapshot, date: snapshot.date || offeringDate || "" } as OfferingData;
  })();

  const { data: liveOfferingData } = useQuery({
    queryKey: ["public_offering", event?.church_id, offeringDate],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("finance_records")
        .select("category, amount, member_name, member_id")
        .eq("church_id", event!.church_id!)
        .eq("date", offeringDate!);
      if (error) throw error;
      const totals: Record<string, number> = {};
      let grand = 0;
      const contributions: Array<{ name: string; amount: number }> = [];
      (data || []).forEach((r) => {
        totals[r.category] = (totals[r.category] || 0) + Number(r.amount);
        grand += Number(r.amount);
        if (r.category?.toLowerCase().includes("tithe") && (r.member_name || r.member_id)) {
          contributions.push({ name: r.member_name || "Member", amount: Number(r.amount) });
        }
      });
      const entries = Object.entries(totals).map(([category, amount]) => ({ category, amount }));
      const titheEntries = entries.filter((e) => e.category.toLowerCase().includes("tithe"));
      const offeringEntries = entries.filter((e) => !e.category.toLowerCase().includes("tithe"));
      const tithesTotal = titheEntries.reduce((s, e) => s + e.amount, 0);
      const sorted = [...contributions].sort((a, b) => a.name.localeCompare(b.name));
      return { entries, titheContributions: sorted, titheEntries, offeringEntries, tithesTotal, total: grand, date: offeringDate! } as OfferingData;
    },
    // Only query live data when there's no snapshot and we have the required info
    enabled: !snapshotData && !!event?.church_id && !!offeringDate,
  });

  const offeringData = snapshotData || liveOfferingData || null;

  const { timeLeft, isPast } = useCountdown(event?.event_date || "", event?.start_time || null);

  if (!eventId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">No event specified.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">Event not found or no longer active.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header: Church logo, name, verse */}
        {church && (
          <div className="flex items-center gap-4 pb-6 mb-6 border-b border-border">
            {church.logo_url ? (
              <img src={church.logo_url} alt="" className="w-14 h-14 rounded-full object-cover" />
            ) : (
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                <Church className="w-7 h-7 text-primary" />
              </div>
            )}
            <div>
              <h2 className="text-lg font-semibold text-foreground">{church.church_name}</h2>
              {church.bible_verse && (
                <p className="text-sm italic text-muted-foreground mt-0.5">&ldquo;{church.bible_verse}&rdquo;{church.bible_reference && ` — ${church.bible_reference}`}</p>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr] gap-8 items-start">
          {/* Left: Flyer image */}
          <div>
            {event.flyer_url ? (
              <img
                src={event.flyer_url}
                alt={event.name}
                className="w-auto max-w-full rounded-2xl"
              />
            ) : (
              <div className="h-64 bg-muted/50 flex items-center justify-center rounded-2xl">
                <Church className="w-16 h-16 text-muted-foreground/30" />
              </div>
            )}

            {/* Countdown */}
            {timeLeft && (
              <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-transparent rounded-2xl p-5 mt-4">
                <p className="text-xs font-semibold text-muted-foreground mb-3 uppercase tracking-widest text-center">Event starts in</p>
                <div className="grid grid-cols-4 gap-2">
                  <div className="bg-background/80 backdrop-blur-sm rounded-xl py-3 text-center shadow-sm">
                    <p className="text-3xl font-extrabold text-foreground tabular-nums">{timeLeft.days}</p>
                    <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mt-1">Days</p>
                  </div>
                  <div className="bg-background/80 backdrop-blur-sm rounded-xl py-3 text-center shadow-sm">
                    <p className="text-3xl font-extrabold text-foreground tabular-nums">{String(timeLeft.hours).padStart(2, "0")}</p>
                    <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mt-1">Hours</p>
                  </div>
                  <div className="bg-background/80 backdrop-blur-sm rounded-xl py-3 text-center shadow-sm">
                    <p className="text-3xl font-extrabold text-foreground tabular-nums">{String(timeLeft.minutes).padStart(2, "0")}</p>
                    <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mt-1">Min</p>
                  </div>
                  <div className="bg-background/80 backdrop-blur-sm rounded-xl py-3 text-center shadow-sm">
                    <p className="text-3xl font-extrabold text-foreground tabular-nums">{String(timeLeft.seconds).padStart(2, "0")}</p>
                    <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mt-1">Sec</p>
                  </div>
                </div>
              </div>
            )}
            {isPast && (
              <div className="bg-muted/50 rounded-2xl p-4 text-center mt-4">
                <p className="text-sm font-semibold text-muted-foreground">This event has started</p>
              </div>
            )}
          </div>

          {/* Right: Event info */}
          <div className="space-y-4">
            <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-transparent rounded-2xl p-5 space-y-4">
              <h1 className="text-2xl font-bold text-foreground">{event.name}</h1>

              <div className="space-y-2 text-sm text-muted-foreground">
                <p className="flex items-center gap-2">
                  <CalendarDays className="w-4 h-4 shrink-0" />
                  {format(parseISO(event.event_date), "EEEE, MMMM d, yyyy")}
                </p>
                {event.start_time && (
                  <p className="flex items-center gap-2">
                    <Clock className="w-4 h-4 shrink-0" />
                    {format(new Date(`2000-01-01T${event.start_time}`), "h:mm a")}
                    {event.end_time && ` – ${format(new Date(`2000-01-01T${event.end_time}`), "h:mm a")}`}
                    <span className="text-xs opacity-60">{Intl.DateTimeFormat("en-US", { timeZoneName: "short" }).formatToParts(new Date()).find(p => p.type === "timeZoneName")?.value}</span>
                  </p>
                )}
                {event.location && (
                  <p className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 shrink-0" />
                    {event.location}
                  </p>
                )}
              </div>

              {event.description && (
                <div className="pt-2">
                  <p className="text-sm text-foreground whitespace-pre-wrap">{event.description}</p>
                </div>
              )}
            </div>

            {/* QR Code + Check-in */}
            <div className="pt-4 flex flex-col items-center md:items-start">
              <p className="text-sm font-medium mb-3">Scan to Check In</p>
              <a
                href={`${window.location.origin}/checkin?event_id=${event.id}`}
                className="block w-fit"
              >
                <QRCodeSVG
                  value={`${window.location.origin}/checkin?event_id=${event.id}`}
                  size={160}
                  level="H"
                />
              </a>
              <div className="mt-3">
                <Button asChild className="rounded-full px-8">
                  <a href={`${window.location.origin}/checkin?event_id=${event.id}`}>Check In</a>
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-4">Powered by iChurchData</p>
            </div>
          </div>
        </div>

        {/* Linked Bulletin — swipeable preview */}
        {bulletin && <BulletinSwiper bulletin={bulletin as any} church={church as any} offeringData={offeringData as any} />}
      </div>
    </div>
  );
}
